import { HttpErrorResponse } from '@angular/common/http';
import { ServiceError } from '../../../../../shared/types/service-error';
import { Unavailable } from '../../../../../shared/types/unavailable';
import { BadRequest } from '../../../../../shared/types/bad-request';
import { NotFound } from '../../../../../shared/types/not-found';
import { notifyMessage } from '../../../../../shared/constants/notify-message';
import { FeasibilityRangeQuota } from '../../../../../shared/interfaces/feasibility.interface';

export class FeasibilityError {
  private _service = 'Feasibility';

  public get message(): string {
    return this.analyze().message;
  }

  constructor(
    public readonly error: unknown,
    public readonly quotas: FeasibilityRangeQuota[]
  ) {}

  public analyze(): Error {
    if (this.isHttpError(this.error)) {
      return this._httpError(this.error);
    }

    if (this.isError(this.error)) {
      return this.error;
    }

    return new ServiceError(this._service);
  }

  public isHttpError(value: unknown): value is HttpErrorResponse {
    return value instanceof HttpErrorResponse;
  }

  public isError(value: unknown): value is Error {
    return value instanceof Error;
  }

  public isQuotaDistributionError(error: HttpErrorResponse) {
    if (error?.error?.msg.includes('required_count should be >= 0')) {
      const regexForQuotaIndex = /data\.quotas\[(\d+)\]/;
      const quotaIndexWithError = Number(
        error.error.msg.match(regexForQuotaIndex)[1]
      );
      const quotaWithError =
        this.quotas[quotaIndexWithError].criteria[0].qualification_name;
      return {
        hasQuotaDistributionError: true,
        message:
          notifyMessage.errorMessage.FEASIBILITY.QUOTA_DISTRIBUTION_ERROR(
            quotaWithError
          ),
      };
    }
    return { hasQuotaDistributionError: false, message: '' };
  }

  private _httpError(error: HttpErrorResponse): Error {
    if (new Unavailable(error).check()) {
      return new Error(
        `${this._service} Service appears to be unavailable at the moment, please contact support.`
      );
    }

    if (new BadRequest(error).check()) {
      const { hasQuotaDistributionError, message } =
        this.isQuotaDistributionError(error);
      const errorMessage = hasQuotaDistributionError
        ? message
        : notifyMessage.errorMessage.FEASIBILITY.FEASIBILITY_NOT_PROCESSED;
      return new Error(errorMessage);
    }

    if (new NotFound(error).check()) {
      return new Error(
        notifyMessage.errorMessage.FEASIBILITY.FEASIBILITY_NOT_PROCESSED
      );
    }
    return error;
  }
}
